import { getIdToken } from "./auth.ts";

const SIGNED_URL_ENDPOINT =
  "https://europe-west3-breathfulnessapp.cloudfunctions.net/getCloudflareStreamSignedUrl";

interface SignedUrlResponse {
  hlsManifestURL: string;
  thumbnailURL: string;
  isLive: boolean;
  duration: number | null;
}

export async function fetchSignedUrl(videoId: string): Promise<SignedUrlResponse> {
  const idToken = await getIdToken();
  const resp = await fetch(SIGNED_URL_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ videoId }),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Signed URL request failed: ${resp.status} ${text}`);
  }
  return (await resp.json()) as SignedUrlResponse;
}

const CF_CUSTOMER_SUBDOMAIN =
  "customer-lriivqt1mrums3sm.cloudflarestream.com";

/**
 * Mount a Cloudflare Stream player into the given container.
 * Returns a teardown function that removes the player.
 *
 * Uses the native iframe embed (same mechanism the official React
 * component uses internally), so we avoid the custom-element src
 * reflection quirk and skip the embed SDK script entirely.
 *
 * The backend signs the URL as
 * `https://customer-XXX.cloudflarestream.com/<token>/manifest/video.m3u8`
 * so we extract the token segment and build the iframe URL with it.
 */
export async function mountStream(
  container: HTMLElement,
  hlsManifestURL: string,
  options: { isLive: boolean; poster?: string }
): Promise<() => void> {
  container.innerHTML = "";

  const tokenMatch = hlsManifestURL.match(
    /\.cloudflarestream\.com\/([^/]+)\//
  );
  const tokenOrId = tokenMatch ? tokenMatch[1] : hlsManifestURL;

  const params = new URLSearchParams();
  params.set("controls", "true");
  params.set("preload", options.isLive ? "auto" : "metadata");
  if (options.isLive) {
    params.set("autoplay", "true");
    params.set("muted", "true");
  }
  if (options.poster) params.set("poster", options.poster);

  const iframe = document.createElement("iframe");
  iframe.src = `https://${CF_CUSTOMER_SUBDOMAIN}/${tokenOrId}/iframe?${params.toString()}`;
  iframe.allow =
    "accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;";
  iframe.allowFullscreen = true;
  iframe.setAttribute("loading", "lazy");
  iframe.style.position = "absolute";
  iframe.style.inset = "0";
  iframe.style.width = "100%";
  iframe.style.height = "100%";
  iframe.style.border = "none";
  container.appendChild(iframe);

  return () => {
    container.innerHTML = "";
  };
}
